import { useDebounceFn } from '@dni-react-hooks/use-debounce-fn'
import { useEffect, useState } from 'react'

/**
 * 觀察 `anchor` 元素的寬度並 debounce 同步給 consumer，
 * 用於 popup / menu 需要對齊 anchor input 寬度的情境（例如 fullWidth AutoComplete / Select）。
 *
 * - `enabled=false` 或 `anchor=null` 時不啟動 ResizeObserver。
 * - 回傳值優先使用 `borderBoxSize[0].inlineSize`，否則 fallback `contentRect.width`。
 * - 預設 100ms debounce，避免高頻 resize 造成 menu 跳動。
 */
export const useAnchorMatchedWidth = (
  anchor: HTMLElement | null,
  enabled: boolean,
): number => {
  const [width, setWidth] = useState(0)
  const debouncedSetWidth = useDebounceFn(setWidth, 100)

  useEffect(function observeAnchorWidth() {
    if (!anchor || !enabled) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const nextWidth = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
      debouncedSetWidth(nextWidth)
    })
    observer.observe(anchor)
    return () => observer.disconnect()
  }, [anchor, enabled, debouncedSetWidth])

  return width
}
